import React, { useState, useEffect } from 'react';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  FolderOpen,
  Plus,
  Edit2,
  Trash2,
  FileText,
  CheckCircle,
  XCircle,
  GripVertical,
  Star
} from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const CategoriasDocumentos = () => {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedCategoria, setSelectedCategoria] = useState(null);
  
  const [formData, setFormData] = useState({
    nombre: '',
    descripcion: '',
    requerido_onboarding: false,
    activo: true
  });

  useEffect(() => {
    loadCategorias();
  }, []);

  const loadCategorias = async () => {
    try {
      setLoading(true);
      const response = await api.get('/onboarding-config/categorias-documentos');
      setCategorias(response.data || []);
    } catch (error) {
      console.error('Error loading categorias:', error);
      toast.error('Error cargando categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      if (!formData.nombre.trim()) {
        toast.error('El nombre es requerido');
        return;
      }

      await api.post('/onboarding-config/categorias-documentos', formData);
      toast.success('Categoría creada');
      setShowCreateDialog(false);
      resetForm();
      loadCategorias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando categoría');
    }
  };

  const handleUpdate = async () => {
    try {
      if (!formData.nombre.trim()) {
        toast.error('El nombre es requerido');
        return;
      }

      await api.put(`/onboarding-config/categorias-documentos/${selectedCategoria.id}`, formData);
      toast.success('Categoría actualizada');
      setShowEditDialog(false);
      resetForm();
      loadCategorias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error actualizando categoría');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar esta categoría de documento?')) return;
    
    try {
      await api.delete(`/onboarding-config/categorias-documentos/${id}`);
      toast.success('Categoría eliminada');
      loadCategorias();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error eliminando categoría');
    }
  };

  const handleToggleRequerido = async (categoria) => {
    try {
      await api.put(`/onboarding-config/categorias-documentos/${categoria.id}`, {
        ...categoria,
        requerido_onboarding: !categoria.requerido_onboarding
      });
      loadCategorias();
    } catch (error) {
      toast.error('Error actualizando categoría');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(categorias);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Actualizar orden localmente
    const reordered = items.map((item, idx) => ({ ...item, orden: idx + 1 }));
    setCategorias(reordered);
    
    // Guardar en backend
    try {
      await api.put('/onboarding-config/categorias-documentos/reorder', {
        orden: reordered.map(c => c.id)
      });
    } catch (error) {
      toast.error('Error guardando orden');
      loadCategorias();
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      descripcion: '',
      requerido_onboarding: false,
      activo: true
    });
    setSelectedCategoria(null);
  };

  const openEditDialog = (categoria) => {
    setSelectedCategoria(categoria);
    setFormData({
      nombre: categoria.nombre,
      descripcion: categoria.descripcion || '',
      requerido_onboarding: categoria.requerido_onboarding || false,
      activo: categoria.activo !== false
    });
    setShowEditDialog(true);
  };

  const requeridosCount = categorias.filter(c => c.requerido_onboarding).length;

  return (
    <div className="p-6 space-y-6" data-testid="categorias-documentos-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FolderOpen className="text-blue-600" />
            Categorías de Documentos
          </h1>
          <p className="text-slate-600 mt-1">
            Configura las categorías de documentos para el legajo de clientes y onboarding
          </p>
        </div>
        <Button onClick={() => setShowCreateDialog(true)} data-testid="create-categoria-btn">
          <Plus size={16} className="mr-2" />
          Nueva Categoría
        </Button>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <FolderOpen className="text-blue-600" size={20} />
            <span className="text-sm font-medium text-slate-700">Total Categorías</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">{categorias.length}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <Star className="text-amber-500" size={20} />
            <span className="text-sm font-medium text-slate-700">Requeridos en Onboarding</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">{requeridosCount}</p>
        </Card>
        <Card className="p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="text-green-600" size={20} />
            <span className="text-sm font-medium text-slate-700">Activas</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{categorias.filter(c => c.activo !== false).length}</p>
        </Card>
      </div>

      {/* Lista de Categorías */}
      <Card className="p-4">
        <h3 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <FileText className="text-slate-600" />
          Categorías Configuradas
        </h3>

        {loading ? (
          <div className="text-center py-8 text-slate-500">Cargando...</div>
        ) : categorias.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <FolderOpen size={48} className="mx-auto mb-4 opacity-30" />
            <p>No hay categorías de documentos configuradas</p>
            <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
              <Plus size={16} className="mr-2" />
              Crear Primera Categoría
            </Button>
          </div>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="categorias">
              {(provided) => (
                <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-2">
                  {categorias.map((categoria, index) => (
                    <Draggable key={categoria.id} draggableId={categoria.id} index={index}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          className={`flex items-center gap-3 p-4 rounded-lg border transition-all ${
                            snapshot.isDragging 
                              ? 'bg-blue-50 border-blue-300 shadow-lg' 
                              : categoria.activo !== false
                                ? 'bg-white border-slate-200 hover:border-slate-300'
                                : 'bg-slate-50 border-slate-200 opacity-60'
                          }`}
                        >
                          {/* Drag Handle */}
                          <div
                            {...provided.dragHandleProps}
                            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
                          >
                            <GripVertical size={18} className="text-slate-400" />
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-900">{categoria.nombre}</span>
                              {categoria.requerido_onboarding && (
                                <Badge className="bg-amber-100 text-amber-700 text-xs">
                                  <Star size={10} className="mr-1" />
                                  Requerido
                                </Badge>
                              )}
                              {categoria.activo === false && (
                                <Badge variant="secondary" className="text-xs">Inactivo</Badge>
                              )}
                            </div>
                            {categoria.descripcion && (
                              <p className="text-sm text-slate-500 mt-1">{categoria.descripcion}</p>
                            )}
                          </div>

                          {/* Toggle Requerido */}
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">Requerido</span>
                            <Switch
                              checked={categoria.requerido_onboarding}
                              onCheckedChange={() => handleToggleRequerido(categoria)}
                            />
                          </div>

                          {/* Acciones */}
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditDialog(categoria)}
                            >
                              <Edit2 size={14} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(categoria.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        )}
      </Card>

      {/* Info */}
      <Card className="p-4 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <FileText className="text-blue-600 mt-1" />
          <div>
            <h4 className="font-semibold text-blue-900">Uso de las Categorías</h4>
            <p className="text-sm text-blue-700 mt-1">
              Las categorías marcadas como "Requeridas" serán solicitadas durante el proceso de onboarding 
              de nuevas empresas. Todas las categorías estarán disponibles en el módulo de Legajo Digital 
              para cargar documentos de clientes existentes.
            </p>
          </div>
        </div>
      </Card>

      {/* Dialog Crear */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="text-blue-600" />
              Nueva Categoría de Documento
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                placeholder="Ej: Contrato Social, DNI, Constancia AFIP..."
                value={formData.nombre}
                onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                placeholder="Descripción opcional de qué documentos incluye esta categoría..."
                value={formData.descripcion}
                onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Requerido en Onboarding</Label>
                <p className="text-xs text-slate-500">Se solicitará al registrar nuevas empresas</p>
              </div>
              <Switch
                checked={formData.requerido_onboarding}
                onCheckedChange={v => setFormData(prev => ({ ...prev, requerido_onboarding: v }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Activo</Label>
                <p className="text-xs text-slate-500">Visible para carga de documentos</p>
              </div>
              <Switch
                checked={formData.activo}
                onCheckedChange={v => setFormData(prev => ({ ...prev, activo: v }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} disabled={!formData.nombre.trim()}>
              Crear Categoría
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="text-blue-600" />
              Editar Categoría
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={e => setFormData(prev => ({ ...prev, nombre: e.target.value }))}
              />
            </div>

            <div>
              <Label>Descripción</Label>
              <Textarea
                value={formData.descripcion}
                onChange={e => setFormData(prev => ({ ...prev, descripcion: e.target.value }))}
                rows={2}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Requerido en Onboarding</Label>
                <p className="text-xs text-slate-500">Se solicitará al registrar nuevas empresas</p>
              </div>
              <Switch
                checked={formData.requerido_onboarding}
                onCheckedChange={v => setFormData(prev => ({ ...prev, requerido_onboarding: v }))}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Activo</Label>
                <p className="text-xs text-slate-500">Visible para carga de documentos</p>
              </div>
              <Switch
                checked={formData.activo}
                onCheckedChange={v => setFormData(prev => ({ ...prev, activo: v }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditDialog(false); resetForm(); }}>
              Cancelar
            </Button>
            <Button onClick={handleUpdate} disabled={!formData.nombre.trim()}>
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CategoriasDocumentos;
